import { useEffect, useRef, useState } from "react";
import { appendCacheBuster, resolveAssetUrl } from "../utils/assetUrl";

export default function ImageUploadField({
  label,
  helper,
  recommended,
  value,
  onChange,
  onUpload,
  onRemove,
  accept = "image/png,image/jpeg,image/webp",
  maxSizeMb = 2,
  allowUrlPaste = true,
  urlLabel = "Or paste URL",
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [localValue, setLocalValue] = useState(value || "");
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    setLocalValue(value || "");
    setPreviewUrl(resolveAssetUrl(value || ""));
  }, [value]);

  const allowedTypes = accept
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  const maxBytes = maxSizeMb * 1024 * 1024;

  const openFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const resetError = () => setError("");

  const validateFile = (file) => {
    if (!file) return "Please choose a file.";
    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      return "Unsupported file type. Use PNG, JPG, or WebP.";
    }
    if (file.size > maxBytes) {
      return `Image is too large. Max ${maxSizeMb} MB.`;
    }
    return "";
  };

  const handleUploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    resetError();
    setUploading(true);
    try {
      const url = await onUpload(file);
      if (url !== undefined) {
        onChange(url || "");
        setPreviewUrl(appendCacheBuster(resolveAssetUrl(url || "")));
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Upload failed. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleUploadFile(file);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      handleUploadFile(file);
    }
  };

  const handleRemove = async () => {
    resetError();
    setUploading(true);
    try {
      const url = await onRemove?.();
      onChange(url || "");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to remove image."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
          {label}
        </label>
        {localValue && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={uploading}
            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Remove
          </button>
        )}
      </div>

      <div
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(false);
        }}
        onDrop={handleDrop}
        className={`rounded-2xl border border-dashed px-4 py-4 transition ${
          dragActive
            ? "border-rose-400 bg-rose-50"
            : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {previewUrl ? (
              <div className="h-16 w-16 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <img
                  src={previewUrl}
                  alt={`${label} preview`}
                  className="h-full w-full object-cover"
                  onError={() =>
                    setError("Preview failed to load. Check the image URL.")
                  }
                />
              </div>
            ) : (
              <div className="h-16 w-16 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-400">
                Preview
              </div>
            )}
            <div className="space-y-1">
              <div className="text-sm font-semibold text-slate-800">
                Drag & drop an image, or upload.
              </div>
              <div className="text-xs text-slate-500">
                {helper || "PNG, JPG, or WebP."}
              </div>
              <div className="text-xs text-slate-500">
                Max {maxSizeMb} MB
                {recommended ? ` · ${recommended}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openFileDialog}
              disabled={uploading}
              className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            {uploading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
            )}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {allowUrlPaste && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowUrlInput((prev) => !prev)}
            className="text-xs font-semibold text-slate-600 underline"
          >
            {urlLabel}
          </button>
          {showUrlInput && (
            <input
              type="text"
              value={localValue}
              onChange={(event) => {
                const next = event.target.value;
                setLocalValue(next);
                onChange(next);
              }}
              placeholder="https://..."
              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
            />
          )}
        </div>
      )}

      {error && <div className="text-xs text-rose-600">{error}</div>}
    </div>
  );
}
